import {
    categoryType,
    useCreateAiWorkersMutation,
    RouteState,
    useUpdateAiWorkersMutation,
    useGetAiWorkersBySourceTypeQuery,
    useTypedSelector,
    useGetDifyWorkflowsQuery,
    useGetCredentialsListQuery,
    useGetAllowedModelsQuery,
    useGetPhoneNumberListQuery,
    useCreateAgentMutation,
    getDefaultNumberFromResponse,
    normalizeFromNumberForApi,
    parseFromNumber,
    type CreateVoiceAgentRequest,
    type TemplateEntry,
    type AllowedModelsTemplateConfig,
    type AllowedModelsResponse,
    type AllowedModelsTtsEntry,
    useEntityResourcePermissions,
    readWebAgentStartUrl,
} from '@circuitry-ai/doc-data';
import { checkType, CtyButton, CtyShowToast, JsonFormatButton, CopyCodeField, optionalCodePayload } from '@circuitry-ai/doc-ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, type Path, type UseFormSetValue } from 'react-hook-form';
import { Input, Select, SelectItem, Textarea, Switch } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useUXGenieTemplateSelection, UXGenieTemplateSelector } from './shared';
import { VoiceAgentContent } from './VoiceAgentContent';
import { WebAgentConfiguration } from './WebAgentConfiguration';
import { sanitizeFirstMessageForApi, sanitizeSttForApi, sanitizeTtsForApi } from './voiceAgentPayloadUtils';

/** Voice agent form values (matches CreateVoiceAgentRequest shape; name comes from parent state). */
export interface VoiceAgentFormValues {
    name: string;
    pipeline_mode: string;
    from_number: string;
    phoneCode?: string;
    phoneNumber?: string;
    stt: { provider: string; model?: string; live_options: { model: string; language: string; smart_format: boolean; punctuate: boolean; numerals: boolean; profanity_filter: boolean; interim_results: boolean } };
    llm: { provider: string; model: string; temperature: number; system_prompt_template: string; system_prompt_template_outbound: string };
    first_message: { mode: string; text: string };
    first_message_outbound: { mode: string; text: string };
    report_only_initial_ttfb: boolean;
    idle_timeout: { enabled: boolean; timeout_seconds: number; retries: number; prompt_templates: string[] };
    filler_words: { enabled: boolean; filler_phrases: string[]; delay_seconds: number };
    summarization_enabled: boolean;
    webhook_urls: string[] | null;
    tools: { hangup_tool: { enabled: boolean }; business_tools: unknown[] };
    context_config: Record<string, unknown>;
    vad: Record<string, unknown>;
    tts: { provider: string; voice_id: string; default_language?: string; configs?: Record<string, TtsLanguageConfig> };
}
export interface TtsLanguageConfig {
    provider: string;
    voice_id: string;
    model_id?: string;
    model?: string;
    voice_settings?: Record<string, unknown>;
}

const VOICE_AGENT_DEFAULT_VALUES = {
    from_number: '',
    stt: { provider: 'deepgram', live_options: { model: 'nova-3-general', language: 'multi' } },
    llm: { provider: 'openai', model: 'gpt-4o-mini', temperature: 0.7, system_prompt_template: 'You are a helpful assistant.', system_prompt_template_outbound: 'You are a helpful assistant.' },
    first_message: { mode: 'speak_first', text: 'Hi! This is an automated assistant calling to help you. Is now a good time to talk?' },
    first_message_outbound: { mode: 'speak_first', text: 'Hi! This is an automated assistant calling to help you. Is now a good time to talk?' },
    tts: { provider: 'elevenlabs', voice_id: '2zRM7PkgwBPiau2jvVXc' },
};
function recordToOption(r: Record<string, string> | undefined): { value: string; label: string } | null {
    if (!r || typeof r !== 'object') return null;
    const entries = Object.entries(r);
    if (entries.length === 0) return null;
    const [label, value] = entries[0];
    return { value: value ?? '', label: label ?? '' };
}
function recordToOptions(r: Record<string, string> | undefined): Array<{ value: string; label: string }> {
    if (!r || typeof r !== 'object') return [];
    return Object.entries(r).map(([label, value]) => ({ label, value: value ?? '' }));
}
function findTtsEntryByProvider(entries: AllowedModelsTtsEntry[], providerValue: string): AllowedModelsTtsEntry | undefined {
    if (!providerValue) return undefined;
    return entries.find((e) => recordToOption(e.provider)?.value === providerValue);
}
function getLlmOptions(allowedModels: AllowedModelsResponse | undefined) {
    const llmEntries = allowedModels?.allowed_models?.llm ?? [];
    const providers = llmEntries
        .map((entry) => recordToOption(entry.provider))
        .filter((p): p is { value: string; label: string } => p != null);
    return { providers, llmEntries };
}
function getSttOptions(allowedModels: AllowedModelsResponse | undefined) {
    const sttEntries = allowedModels?.allowed_models?.stt ?? [];
    const providers = sttEntries
        .map((entry) => recordToOption(entry.provider))
        .filter((p): p is { value: string; label: string } => p != null);
    return { providers, sttEntries };
}
function getTtsOptions(ttsEntries: AllowedModelsTtsEntry[] | undefined) {
    return (ttsEntries ?? [])
        .map((entry) => recordToOption(entry.provider))
        .filter((p): p is { value: string; label: string } => p != null)
        .filter((p) => p.value !== 'dynamic');
}
function getTtsVoiceOptions(ttsEntries: AllowedModelsTtsEntry[] | undefined): Array<{ provider: string; voice_id: string; model_id?: string; model?: string }> {
    const options: Array<{ provider: string; voice_id: string; model_id?: string; model?: string }> = [];
    for (const entry of ttsEntries ?? []) {
        const provider = recordToOption(entry.provider)?.value ?? '';
        const voices = recordToOptions(entry.voice_id ?? entry.voice);
        const models = recordToOptions(entry.model_id ?? entry.model);
        for (const voice of voices) {
            options.push({ provider, voice_id: voice.value, model_id: models[0]?.value, model: models[0]?.value });
        }
    }
    return options;
}

function getLanguageLabelsFromAllowedModels(ttsEntries: Array<{ language?: Record<string, string> }> | undefined): Record<string, string> {
    const labels: Record<string, string> = {};
    if (!ttsEntries) return labels;
    for (const entry of ttsEntries) {
        if (entry.language && typeof entry.language === 'object') {
            Object.entries(entry.language).forEach(([code, label]) => { if (code && label) labels[code] = label; });
        }
    }
    return labels;
}

function getAllowedTtsLanguagesFromTemplates(templates: TemplateEntry[], languageLabels: Record<string, string>): Array<{ value: string; label: string }> {
    const codes = new Set<string>();
    for (const t of templates) {
        const configs = t.config?.tts?.configs;
        if (configs && typeof configs === 'object') Object.keys(configs).forEach((k) => codes.add(k));
    }
    return Array.from(codes).map((value) => ({ value, label: languageLabels[value] ?? value }));
}

function applyTemplateConfig(setValue: UseFormSetValue<VoiceAgentFormValues>, config: AllowedModelsTemplateConfig): void {
    if (config.stt) {
        setValue('stt.provider' as Path<VoiceAgentFormValues>, config.stt.provider ?? '');
        if (config.stt.live_options) {
            const loModel = config.stt.live_options.model ?? '';
            setValue('stt.live_options.model' as Path<VoiceAgentFormValues>, loModel);
            setValue('stt.live_options.language' as Path<VoiceAgentFormValues>, config.stt.live_options.language ?? 'multi');
            if (loModel) setValue('stt.model' as Path<VoiceAgentFormValues>, loModel);
        }
        if (config.stt.model != null && config.stt.model !== '') {
            setValue('stt.live_options.model' as Path<VoiceAgentFormValues>, config.stt.model);
            setValue('stt.model' as Path<VoiceAgentFormValues>, config.stt.model);
        }
    }
    if (config.tts) {
        setValue('tts.provider', config.tts.provider ?? '');
        setValue('tts.voice_id', config.tts.voice_id ?? '');
        setValue('tts.default_language', config.tts.default_language ?? 'en');
        const configs = config.tts.configs as Record<string, TtsLanguageConfig> | undefined;
        if (configs && Object.keys(configs).length > 0) setValue('tts.configs', configs);
    }
    if (config.llm) {
        setValue('llm.provider', config.llm.provider ?? '');
        setValue('llm.model', config.llm.model ?? '');
        setValue('llm.temperature', config.llm.temperature ?? 0.7);
        setValue('llm.system_prompt_template', config.llm.system_prompt_template ?? '');
        setValue('llm.system_prompt_template_outbound', config.llm.system_prompt_template_outbound ?? '');
    }
    if (config.first_message) {
        setValue('first_message.mode', config.first_message.mode ?? 'speak_first');
        setValue('first_message.text', config.first_message.text ?? '');
    }
}
const PHONE_PROVIDER_OPTIONS = [
    { value: 'twilio_US', provider: 'twilio', country_iso: 'US', label: '+1 (US • Twilio)' },
] as const;

export type AgentCreationType = 'new' | 'existing' | 'voice_agent' | 'web_agent';

export interface AgentCreationProps {
    agent?: any;
    onCreationTypeChange?: (creationType: AgentCreationType) => void;
}

export function AgentCreation({ agent: agentProp, onCreationTypeChange }: Readonly<AgentCreationProps> = {}) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const token = useTypedSelector((state) => state.auth.token);
    const [skip, setSkip] = useState<boolean>(true);

    const { mode } = useParams<{ mode: string; }>();
    const [createAiWorkers, { isLoading: isAgentCreationLoading }] = useCreateAiWorkersMutation();
    const [updateAiWorkers, { isLoading: isAgentUpdateLoading }] = useUpdateAiWorkersMutation();

    const { data: actionsList, isFetching, isLoading } = useGetAiWorkersBySourceTypeQuery('action', { skip, refetchOnMountOrArgChange: true });

    useEffect(() => {
        if (token && skip) {
            setSkip(false);
        }
    }, [skip, token]);

    const agent = agentProp ?? (location?.state as { agent?: any } | null)?.agent ?? null;
    const isCreateMode = mode === RouteState.CREATE;
    const { canUpdate } = useEntityResourcePermissions('agent', isCreateMode ? undefined : agent?.aiworker_id);

    const [agentCode, setAgentCode] = useState<string>('');
    const [agentName, setAgentName] = useState<string>('');
    const [publicDescription, setPublicDescription] = useState<string>('');
    const [command, setCommand] = useState<string>('');
    const [commandError, setCommandError] = useState<boolean>(false);
    const [toolsList, setToolsList] = useState<{ label: string, value: string }[]>([]);
    type Instruction = { goal: string, tools: string, include_in_response?: boolean; };
    const [instructions, setInstructions] = useState<Instruction[]>([{ tools: '', goal: '', include_in_response: false }]);

    // Workflow selection states
    const [creationType, setCreationType] = useState<AgentCreationType>('new');
    useEffect(() => {
        onCreationTypeChange?.(creationType);
    }, [creationType, onCreationTypeChange]);
    /** When loading an agent in update mode, set from aiworkers_attrs voice_agent_id. Used to show tabs until save. */
    const [voiceAgentIdFromAttrs, setVoiceAgentIdFromAttrs] = useState<string | null>(null);
    /** Handler to run voice agent save first (tabs form); set by VoiceAgentContent when update + tabs. */
    const voiceSaveHandlerRef = useRef<(() => Promise<boolean>) | null>(null);

    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
    const [selectedCredentialId, setSelectedCredentialId] = useState<string>('');
    const [webAgentStartUrl, setWebAgentStartUrl] = useState<string>('');
    const { data: workflowsResponse, isLoading: isLoadingWorkflows } = useGetDifyWorkflowsQuery(undefined, { skip: !token, refetchOnMountOrArgChange: true });
    const workflowsData = workflowsResponse || [];
    const { data: credentialsList, isLoading: isCredentialsListLoading } = useGetCredentialsListQuery(undefined, { skip: !token, refetchOnMountOrArgChange: true });
    const credentialOptions = Array.isArray(credentialsList) ? credentialsList : [];
    const agentTypeOptions = [
        {
            label: 'Agent',
            value: 'new'
        },
        {
            label: 'Workflow',
            value: 'existing'
        },
        {
            label: 'Voice Agent',
            value: 'voice_agent'
        },
        {
            label: 'Web Agent',
            value: 'web_agent'
        }
    ]

    // UXGenie Template selection - using shared hook
    const [initialTemplateId, setInitialTemplateId] = useState<string>('');
    const {
        selectedUXGenieWorkerId,
        setSelectedUXGenieWorkerId,
        uxgenieWorkers,
        selectedUXGenieTemplateId,
        setSelectedUXGenieTemplateId,
        uxgenieTemplates
    } = useUXGenieTemplateSelection(initialTemplateId);

    // Voice agent form (from CreateAgent - except agent name which uses agentName state)
    const isVoiceAgent = creationType === 'voice_agent';
    const {
        control: voiceControl,
        setValue: voiceSetValue,
        getValues: voiceGetValues,
        watch: voiceWatch,
        reset: voiceReset,
    } = useForm<VoiceAgentFormValues>({ defaultValues: VOICE_AGENT_DEFAULT_VALUES, mode: 'onChange' });

    const [createVoiceAgentMutation, { isLoading: isCreatingVoiceAgent }] = useCreateAgentMutation();
    const { data: allowedModels, isLoading: allowedModelsLoading } = useGetAllowedModelsQuery(undefined, { skip: !isVoiceAgent });
    const templates: TemplateEntry[] = useMemo(() => allowedModels?.allowed_models?.templates ?? [], [allowedModels?.allowed_models?.templates]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [showAddTtsConfig, setShowAddTtsConfig] = useState(false);
    const [addTtsProvider, setAddTtsProvider] = useState('');
    const [addTtsLanguage, setAddTtsLanguage] = useState('');
    const [addTtsVoiceId, setAddTtsVoiceId] = useState('');

    const phoneProviderCountry = voiceWatch('phoneCode');
    const selectedPhoneOption = PHONE_PROVIDER_OPTIONS.find((o) => o.value === phoneProviderCountry);
    const { data: phoneListData, isFetching: isVoiceAgentPhoneListFetching } = useGetPhoneNumberListQuery(
        selectedPhoneOption ? { provider: selectedPhoneOption.provider, country_iso: selectedPhoneOption.country_iso } : undefined,
        { skip: !isVoiceAgent || !selectedPhoneOption }
    );

    const llmProvider = voiceWatch('llm.provider');
    const sttProvider = voiceWatch('stt.provider');
    const llmOpts = useMemo(() => getLlmOptions(allowedModels), [allowedModels]);
    const sttOpts = useMemo(() => getSttOptions(allowedModels), [allowedModels]);
    const ttsEntries = useMemo(() => allowedModels?.allowed_models?.tts ?? [], [allowedModels?.allowed_models?.tts]);
    const ttsOpts = useMemo(() => getTtsOptions(ttsEntries), [ttsEntries]);
    const llmModelsForProvider = useMemo(() => {
        if (!llmProvider) return [];
        const entry = llmOpts.llmEntries.find((e) => recordToOption(e.provider)?.value === llmProvider);
        return recordToOptions(entry?.model);
    }, [llmOpts.llmEntries, llmProvider]);
    const sttModelsForProvider = useMemo(() => {
        if (!sttProvider) return [];
        const entry = sttOpts.sttEntries.find((e) => recordToOption(e.provider)?.value === sttProvider);
        return recordToOptions(entry?.model);
    }, [sttOpts.sttEntries, sttProvider]);
    const ttsConfigs = voiceWatch('tts.configs');
    const ttsLanguageLabels = useMemo(() => getLanguageLabelsFromAllowedModels(allowedModels?.allowed_models?.tts), [allowedModels?.allowed_models?.tts]);
    const allowedTtsLangOptions = useMemo(() => getAllowedTtsLanguagesFromTemplates(templates, ttsLanguageLabels), [templates, ttsLanguageLabels]);
    const ttsAllVoiceOptions = useMemo(() => getTtsVoiceOptions(ttsEntries), [ttsEntries]);


    useEffect(() => {
        if (!isVoiceAgent) return;
        voiceReset(VOICE_AGENT_DEFAULT_VALUES);
        setSelectedTemplateId('');
        setShowAddTtsConfig(false);
        setAddTtsProvider('');
        setAddTtsLanguage('');
        setAddTtsVoiceId('');
    }, [isVoiceAgent, voiceReset]);

    const handleVoiceTemplateChange = (templateId: string) => {
        setSelectedTemplateId(templateId);
        const template = templates.find((t) => t.id === templateId);
        if (template?.config) applyTemplateConfig(voiceSetValue, template.config);
    };

    useEffect(() => {
        if (!isVoiceAgent || !selectedPhoneOption || !phoneListData) return;
        const raw = getDefaultNumberFromResponse(phoneListData);
        voiceSetValue('from_number', parseFromNumber(raw || null).phoneNumber);
    }, [isVoiceAgent, selectedPhoneOption, phoneListData, voiceSetValue]);

    useEffect(() => {
        if (!isVoiceAgent || !allowedModels || allowedModelsLoading) return;
        const firstLlm = allowedModels.allowed_models?.llm?.[0];
        const firstLlmProvider = recordToOption(firstLlm?.provider)?.value;
        const firstLlmModel = recordToOptions(firstLlm?.model)?.[0]?.value;
        if (firstLlmProvider) voiceSetValue('llm.provider', firstLlmProvider);
        if (firstLlmModel) voiceSetValue('llm.model', firstLlmModel);

        const firstStt = allowedModels.allowed_models?.stt?.[0];
        const firstSttProvider = recordToOption(firstStt?.provider)?.value;
        const firstSttModel = recordToOptions(firstStt?.model)?.[0]?.value;
        if (firstSttProvider) voiceSetValue('stt.provider', firstSttProvider);
        if (firstSttModel) {
            voiceSetValue('stt.live_options.model', firstSttModel);
            voiceSetValue('stt.model', firstSttModel);
        }

        if (ttsEntries.length) {
            const existingConfigs = voiceGetValues('tts.configs');
            if (!existingConfigs || Object.keys(existingConfigs).length === 0) {
                const first = ttsEntries[0];
                voiceSetValue('tts.provider', recordToOption(first.provider)?.value ?? '');
                voiceSetValue('tts.voice_id', recordToOptions(first.voice_id ?? first.voice)?.[0]?.value ?? '');
            }
        }
    }, [isVoiceAgent, allowedModels, allowedModelsLoading, ttsEntries, voiceSetValue, voiceGetValues]);

    function buildVoicePayload(values: VoiceAgentFormValues): CreateVoiceAgentRequest {
        const resolvedFromNumber = values.from_number?.trim()
            ? normalizeFromNumberForApi(values.from_number.trim())
            : null;
        let ttsPayload: CreateVoiceAgentRequest['tts'];
        if (values.tts?.provider === 'dynamic') {
            const rawDl = values.tts.default_language;
            const dl = typeof rawDl === 'string' ? rawDl.trim() : '';
            ttsPayload = dl ? { provider: 'dynamic', default_language: dl } : { provider: 'dynamic' };
        } else {
            const hasInputVoice = !!(values.tts?.voice_id && String(values.tts.voice_id).trim() !== '');
            const sanitized = sanitizeTtsForApi(values.tts);
            if (sanitized && hasInputVoice) {
                ttsPayload = sanitized as CreateVoiceAgentRequest['tts'];
            } else if (ttsEntries[0]) {
                const firstEntry = ttsEntries[0];
                ttsPayload = {
                    provider: recordToOption(firstEntry.provider)?.value ?? 'elevenlabs',
                    voice_id: recordToOptions(firstEntry.voice_id ?? firstEntry.voice)?.[0]?.value ?? 'default',
                };
            } else ttsPayload = { provider: 'elevenlabs', voice_id: 'default' };
            
        }
        const {
            phoneCode: _pc,
            phoneNumber: _pn,
            from_number: _fromForm,
            tts: _t,
            stt: _stt,
            first_message: _fm,
            first_message_outbound: _fmo,
            ...rest
        } = values;
        const sttPayload = sanitizeSttForApi(values.stt);
        return {
            ...rest,
            name: agentName,
            from_number: resolvedFromNumber,
            ...(sttPayload ? { stt: sttPayload as CreateVoiceAgentRequest['stt'] } : {}),
            tts: ttsPayload,
            first_message: sanitizeFirstMessageForApi(values.first_message),
            first_message_outbound: sanitizeFirstMessageForApi(values.first_message_outbound),
        } as CreateVoiceAgentRequest;
    }

    useEffect(() => {

        if (agent) {
            const result: Record<string, any> = {};
            agent?.aiworkers_attrs.forEach((item: any) => {
                result[item?.attr_name] = item.attr_json !== null ? item.attr_json : item.attr_value;
            });

            setAgentCode(agent.code ?? '');
            setAgentName(agent.name ?? '');
            setPublicDescription(agent.description ?? '');

            const commandValue = result?.command;
            if (commandValue && typeof commandValue === 'object' && Object.keys(commandValue).length > 0) {
                setCommand(JSON.stringify(commandValue, null, 2));
            } else if (typeof commandValue === 'string' && commandValue.trim()) {
                setCommand(commandValue);
            }

            // Check if agent has workflow_id and credential_id (created from existing)
            const workflowId = result?.workflow_id;
            if (workflowId) {
                setCreationType('existing');
                setSelectedWorkflowId(workflowId);
                const credId = result?.credential_id;
                if (credId) setSelectedCredentialId(credId);
            }

            // Check if agent has voice_agent_id (created as voice agent) – show tabs in update
            const voiceAgentId = result?.voice_agent_id;
            if (voiceAgentId != null && String(voiceAgentId).trim() !== '') {
                setCreationType('voice_agent');
                setVoiceAgentIdFromAttrs(String(voiceAgentId).trim());
            } else {
                setVoiceAgentIdFromAttrs(null);
            }

            // Check if agent has a web_agent_spec (created as web agent)
            const webAgentSpecAttr = result?.web_agent_spec;
            if (webAgentSpecAttr && typeof webAgentSpecAttr === 'object') {
                setCreationType('web_agent');
                setWebAgentStartUrl(readWebAgentStartUrl(webAgentSpecAttr));
            }

            // Check if agent has uxgenie_template (which now stores aiworker_usage_id)
            const uxgenieTemplateId = result?.uxgenie_template;
            if (uxgenieTemplateId) {
                setInitialTemplateId(uxgenieTemplateId);
            }

            const existingFlows = result?.flow?.flows ?? [];
            setInstructions(
                existingFlows.map((flow: any) => ({
                    goal: flow?.goal ?? '',
                    tools: flow?.tools ?? '',
                    include_in_response: flow?.include_in_response ?? false,
                }))
            );
        }
    }, [agent]);

    useEffect(() => {
        if (actionsList && !(isLoading || isFetching)) {
            const toolsListData = (actionsList ?? [])?.map((item: any) => {
                return {
                    label: item?.name,
                    value: item?.name
                };
            });
            setToolsList(toolsListData);
        }
    }, [actionsList, isLoading, isFetching]);


    const validateJSON = (value: string): boolean => {
        try { JSON.parse(value); return true; } catch { return false; }
    };

    const handleSave = async () => {
        try {
            if (command.trim() && !validateJSON(command)) {
                setCommandError(true);
                CtyShowToast(checkType.error, t('Command field contains invalid JSON'));
                return;
            }
            setCommandError(false);

            // Voice agent: create voice agent via API when needed, then create/update ai worker with voice_agent_id
            if (creationType === 'voice_agent') {
                const trimmedName = (agentName ?? '').trim();
                if (!trimmedName) {
                    CtyShowToast(checkType.error, t('Please enter an agent name.'));
                    return;
                }
                let assistantId: string;
                if (isCreateMode) {
                    const payload = buildVoicePayload(voiceGetValues());
                    const created = await createVoiceAgentMutation(payload).unwrap();
                    assistantId = created.id ?? (created as { assistant_id?: string }).assistant_id ?? '';
                    if (!assistantId) {
                        CtyShowToast(checkType.error, t('Voice agent was created but no ID returned.'));
                        return;
                    }
                    const aiworkersAttrs = [{ attr_name: 'voice_agent_id', attr_value: assistantId, attr_json: null as null }];
                    const creatAgentPayload = {
                        category: categoryType.AGENT,
                        type: 'product',
                        sub_type: 'Voice Agent',
                        name: agentName,
                        description: publicDescription,
                        aiworkers_attrs: aiworkersAttrs,
                        aiworker_id:assistantId,
                        ...optionalCodePayload(agentCode),
                    };
                    await createAiWorkers(creatAgentPayload).unwrap();
                    CtyShowToast(checkType.success, t('Agent created successfully. {{name}} is ready to take calls.', { name: created.name ?? trimmedName }));
                    navigate('/agents');
                } else {
                    // Update mode: if we have the tabs form (voiceAgentIdFromAttrs), run voice save first, then update ai worker
                    let resolvedAssistantId: string;
                    if (voiceAgentIdFromAttrs) {
                        const runVoiceSave = voiceSaveHandlerRef.current;
                        if (runVoiceSave) {
                            const voiceSaveOk = await runVoiceSave();
                            if (!voiceSaveOk) return;
                        }
                        resolvedAssistantId = voiceAgentIdFromAttrs;
                    } else {
                        const payload = buildVoicePayload(voiceGetValues());
                        const created = await createVoiceAgentMutation(payload).unwrap();
                        resolvedAssistantId = created.id ?? (created as { assistant_id?: string }).assistant_id ?? '';
                        if (!resolvedAssistantId) {
                            CtyShowToast(checkType.error, t('Voice agent was created but no ID returned.'));
                            return;
                        }
                    }
                    const aiworkersAttrs = [{ attr_name: 'voice_agent_id', attr_value: resolvedAssistantId, attr_json: null as null }];
                    const payloadUpdate = { name: agentName, description: publicDescription, sub_type: 'Voice Agent', aiworkers_attrs: aiworkersAttrs };
                    const response = await updateAiWorkers({ payLoad: payloadUpdate, aiworker_id: agent?.aiworker_id ?? '' }).unwrap();
                    navigate(`${location.pathname}${location.search}`, { state: { agent: response?.data } });
                    CtyShowToast(checkType.success, response?.message ?? t('Agent updated successfully.'));
                }
                return;
            }

            // Validation for existing workflow selection
            if (creationType === 'existing' && !selectedWorkflowId) {
                CtyShowToast(checkType.error, t('Please select a workflow'));
                return;
            }
            if (creationType === 'existing' && !selectedCredentialId) {
                CtyShowToast(checkType.error, t('Please select a credential'));
                return;
            }
            if (creationType === 'web_agent' && !webAgentStartUrl.trim()) {
                CtyShowToast(checkType.error, t('Please enter a Start URL'));
                return;
            }

            const aiworkersAttrs: any[] = [];

            // Add workflow_id, workflow_type (mode), and credential_id if creating from existing
            if (creationType === 'existing' && selectedWorkflowId) {
                aiworkersAttrs.push({
                    attr_name: 'workflow_id',
                    attr_value: selectedWorkflowId,
                    attr_json: null
                });
                const selectedWorkflow = workflowsData.find((w: any) => w.workflow_id === selectedWorkflowId);
                if (selectedWorkflow?.mode != null) {
                    aiworkersAttrs.push({
                        attr_name: 'workflow_type',
                        attr_value: String(selectedWorkflow.mode),
                        attr_json: null
                    });
                }
                if (selectedCredentialId) {
                    aiworkersAttrs.push({
                        attr_name: 'credential_id',
                        attr_value: selectedCredentialId,
                        attr_json: null
                    });
                }
            }

            // Add flow configuration if creating new agent
            if (creationType === 'new') {
                aiworkersAttrs.push({
                    attr_name: 'flow',
                    attr_value: null,
                    attr_json: {
                        circuit_name: agentName,
                        flows: instructions.map(inst => ({
                            goal: inst.goal,
                            tools: Array.isArray(inst.tools) ? inst.tools : [inst.tools],
                            include_in_response: inst.include_in_response ?? false,
                        }))
                    },
                });
            }

            if (creationType === 'web_agent') {
                aiworkersAttrs.push({ attr_name: 'web_agent_spec', attr_value: null, attr_json: { startUrl: webAgentStartUrl.trim() } });
            }

            // Add UXGenie template if selected
            if (selectedUXGenieTemplateId) {
                aiworkersAttrs.push({
                    attr_name: 'uxgenie_template',
                    attr_value: selectedUXGenieTemplateId,
                    attr_json: null
                });
            }

            if (command.trim()) {
                aiworkersAttrs.push({
                    attr_name: 'command',
                    attr_value: command.trim(),
                    attr_json: null,
                });
            }

            const payload: Record<string, any> = {
                name: agentName,
                description: publicDescription,
                sub_type: creationType,
                aiworkers_attrs: aiworkersAttrs,
                ...(isCreateMode ? optionalCodePayload(agentCode) : {}),
            };

            if (isCreateMode) {
                const creatAgentPayload = {
                    category: categoryType.AGENT,
                    type: 'product',
                    ...payload
                };
                const response = await createAiWorkers(creatAgentPayload).unwrap();
                CtyShowToast(checkType.success, response?.message ?? 'Agent created successfully');
                navigate('/agents');
            } else {
                const response = await updateAiWorkers({ payLoad: payload, aiworker_id: agent?.aiworker_id }).unwrap();
                navigate(`${location.pathname}${location.search}`, { state: { agent: response?.data } });
                CtyShowToast(checkType.success, response?.message ?? 'Agent updated successfully');
            }
        }
        catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message || 'Save Operation failed');
        }
    };

    const updateInstruction = (index: number, field: keyof Instruction, value: string | boolean) => {
        setInstructions(prev => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const handleAddInstruction = () => {
        setInstructions(prev => [...prev, { goal: '', tools: '', include_in_response: false }]);
    };

    const handleRemoveInstruction = (index: number) => {

        if (instructions.length <= 1) return null;

        const remove = (index: number) => {
            setInstructions(prev => {
                const next = [...prev];
                next.splice(index, 1);
                return next;
            });
        };

        return (
            <CtyButton size="sm" className='border-1 font-medium' variant="bordered" onPress={() => remove(index)}>
                Remove
            </CtyButton>
        );
    };

    return (
        <div className='flex flex-col h-full gap-3 pb-3'>
            <div className="flex-1 overflow-y-auto w-full">
                <div className='flex flex-col gap-3'>
                    {!(creationType === 'voice_agent' && !isCreateMode && voiceAgentIdFromAttrs) && (
                    <div className="md:w-[70%] flex flex-col gap-3">
                        <CopyCodeField
                            label="Code"
                            value={agentCode}
                            layout="horizontal"
                            isReadOnly={!isCreateMode}
                            isRequired={false}
                            subText="Must be at least 8 alphanumeric characters"
                            onValueChange={isCreateMode ? setAgentCode : undefined}
                        />
                        <div className="flex flex-row gap-3">
                            <label htmlFor={'agent_name'} className="flex flex-col w-[30%]">
                                <span className='text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal'>{t(`Agent name`)}<span className="text-danger">*</span></span>
                            </label>
                            <div className="flex-1">
                                <Input
                                    id={"agent_name"}
                                    type={'text'}
                                    size="sm"
                                    aria-label="agent_name"
                                    value={agentName}
                                    isRequired={true}
                                    onValueChange={setAgentName}
                                />
                            </div>
                        </div>
                        {!isCreateMode && <div className="flex flex-row gap-3">
                            <label htmlFor={'worker_id'} className="flex flex-col w-[30%]">
                                <span className='text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal'>{t(`Worker ID`)}</span>
                            </label>
                            <div className="flex-1">
                                <Input
                                    id={"worker_id"}
                                    type={'text'}
                                    size="sm"
                                    aria-label="worker_id"
                                    isReadOnly={true}
                                    value={agent?.aiworker_id ?? ''}
                                />
                            </div>
                        </div>}
                        <div className="flex flex-row gap-3">
                            <label htmlFor={'public_description'} className="flex flex-col w-[30%]">
                                <span className='text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal'>{t(`Public description`)}<span className="text-danger">*</span></span>
                                <div className='font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]'>{t(`Write a short description`)}</div>
                            </label>
                            <div className="flex-1">
                                <Textarea
                                    id={'public_description'}
                                    minRows={5}
                                    isRequired={true}
                                    maxRows={7}
                                    aria-label="public_description"
                                    value={publicDescription}
                                    onValueChange={setPublicDescription}
                                />
                            </div>
                        </div>
                        <div className="flex flex-row gap-3">
                            <label htmlFor={'command'} className="flex flex-col w-[30%]">
                                <span className='text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal'>{t(`Command`)}</span>
                                <div className='font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]'>{t(`JSON command schema`)}</div>
                            </label>
                            <div className="flex-1">
                                <Textarea
                                    id={'command'}
                                    minRows={3}
                                    maxRows={10}
                                    aria-label="command"
                                    value={command}
                                    isInvalid={commandError}
                                    errorMessage={commandError ? t('Invalid JSON') : ''}
                                    label={
                                        <div className="flex items-center justify-between w-full gap-2">
                                            <span>{t('JSON')}</span>
                                            <JsonFormatButton value={command} onChange={setCommand} disabled={commandError} />
                                        </div>
                                    }
                                    onValueChange={(val) => {
                                        setCommand(val);
                                        if (commandError) setCommandError(false);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Agent Creation Type Selection */}
                    <div className="md:w-[70%] flex flex-col gap-3">
                      {(creationType!=='voice_agent' || isCreateMode) && <div className="flex flex-row gap-3">
                            <label className="flex flex-col w-[30%]">
                                <span className='text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal'>
                                    {t('Agent Type')}<span className="text-danger">*</span>
                                </span>
                                <div className='font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]'>
                                    {t('Choose how to create your agent')}
                                </div>
                            </label>
                            <div className="flex-1">
                                <Select
                                    id="creation-type"
                                    size="sm"
                                    aria-label="Agent Creation Type"
                                    selectedKeys={new Set([creationType])}
                                    onSelectionChange={(keys) => {
                                        const value = String([...keys][0]) as AgentCreationType;
                                        setCreationType(value);
                                        if (value === 'new') {
                                            setSelectedWorkflowId('');
                                            setSelectedCredentialId('');
                                        }
                                    }}
                                >
                                    {agentTypeOptions.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </Select>
                            </div>
                        </div>}

                        {/* Workflow Selection - Show only when "existing" is selected */}
                        {creationType === 'existing' && (
                            <div className="flex flex-row gap-3">
                                <label className="flex flex-col w-[30%]">
                                    <span className='text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal'>
                                        {t('Select Workflow')}<span className="text-danger">*</span>
                                    </span>
                                    <div className='font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]'>
                                        {t('Choose a workflow template')}
                                    </div>
                                </label>
                                <div className="flex-1">
                                    <Select
                                        id="workflow-select"
                                        size="sm"
                                        aria-label="Select Workflow"
                                        placeholder={isLoadingWorkflows ? t('Loading workflows...') : t('Select a workflow')}
                                        selectedKeys={selectedWorkflowId ? new Set([selectedWorkflowId]) : new Set()}
                                        onSelectionChange={(keys) => setSelectedWorkflowId(String([...keys][0]))}
                                        isLoading={isLoadingWorkflows}
                                        isDisabled={isLoadingWorkflows}
                                    >
                                        {workflowsData.map((workflow: any) => (
                                            <SelectItem key={workflow.workflow_id} value={workflow.workflow_id}>
                                                {workflow.workflow_name}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        )}

                        {/* Credential Selection - Show only when "existing" is selected (mandatory) */}
                        {creationType === 'existing' && (
                            <div className="flex flex-row gap-3">
                                <label className="flex flex-col w-[30%]">
                                    <span className='text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal'>
                                        {t('Select Credential')}<span className="text-danger">*</span>
                                    </span>
                                    <div className='font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]'>
                                        {t('Choose a credential for the workflow')}
                                    </div>
                                </label>
                                <div className="flex-1">
                                    <Select
                                        id="credential-select"
                                        size="sm"
                                        aria-label="Select Credential"
                                        placeholder={isCredentialsListLoading ? t('Loading credentials...') : t('Select a credential')}
                                        selectedKeys={selectedCredentialId ? new Set([selectedCredentialId]) : new Set()}
                                        onSelectionChange={(keys) => setSelectedCredentialId(String([...keys][0] ?? ''))}
                                        isLoading={isCredentialsListLoading}
                                        isDisabled={isCredentialsListLoading}
                                    >
                                        {credentialOptions.map((credential: any) => (
                                            <SelectItem key={credential.credential_id} value={credential.credential_id}>
                                                {credential.name}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Web Agent step configuration - shown when the Web Agent type is selected */}
                    {creationType === 'web_agent' && (
                        <div className="md:w-[70%] flex flex-col gap-3">
                            <WebAgentConfiguration startUrl={webAgentStartUrl} onChange={setWebAgentStartUrl} isReadOnly={!isCreateMode && !canUpdate} />
                        </div>
                    )}

                    {/* UXGenie Template Selection - Using shared component */}
                   {creationType!=='voice_agent' && creationType!=='web_agent' && <div className="md:w-[70%] flex flex-col gap-3">
                        <UXGenieTemplateSelector
                            selectedUXGenieWorkerId={selectedUXGenieWorkerId}
                            onWorkerChange={setSelectedUXGenieWorkerId}
                            uxgenieWorkers={uxgenieWorkers}
                            selectedUXGenieTemplateId={selectedUXGenieTemplateId}
                            onTemplateChange={setSelectedUXGenieTemplateId}
                            uxgenieTemplates={uxgenieTemplates}
                            layout="inline"
                        />
                    </div>}
                    {/* Voice agent: create form (create / update without saved voice agent) or update form with tabs (update with voice_agent_id) */}
                    {creationType === 'voice_agent' && (
                        <div className="w-full flex flex-col gap-3">
                            <VoiceAgentContent
                                mode={isCreateMode ? 'create' : 'update'}
                                voiceAgentId={voiceAgentIdFromAttrs}
                                basicInfo={
                                    !isCreateMode && voiceAgentIdFromAttrs
                                        ? {
                                            agentName,
                                            workerId: agent?.aiworker_id ?? '',
                                            description: publicDescription,
                                            onAgentNameChange: setAgentName,
                                            onDescriptionChange: setPublicDescription,
                                            code: agent?.code ,
                                        }
                                        : undefined
                                }
                                onRegisterVoiceSaveHandler={!isCreateMode && voiceAgentIdFromAttrs ? (handler) => { voiceSaveHandlerRef.current = handler; } : undefined}
                                createFormProps={{
                                    control: voiceControl,
                                    setValue: voiceSetValue,
                                    getValues: voiceGetValues,
                                    watch: voiceWatch,
                                allowedModelsLoading,
                                    templates,
                                    selectedTemplateId,
                                    onTemplateChange: handleVoiceTemplateChange,
                                    showAddTtsConfig,
                                    setShowAddTtsConfig,
                                    addTtsProvider,
                                    setAddTtsProvider,
                                    addTtsLanguage,
                                    setAddTtsLanguage,
                                    addTtsVoiceId,
                                    setAddTtsVoiceId,
                                    llmOpts,
                                    sttOpts,
                                    ttsOpts,
                                    llmModelsForProvider,
                                    sttModelsForProvider,
                                    ttsConfigs,
                                    allowedTtsLangOptions,
                                    ttsAllVoiceOptions,
                                    voiceAgentPhoneListData: phoneListData,
                                    voiceAgentPhoneListFetching: isVoiceAgentPhoneListFetching,
                                }}
                            />
                        </div>
                    )}

                    {creationType === 'new' && (
                        <div>
                        <div className=" font-semibold text-base leading-6 tracking-normal">Task Configuration</div>
                        <div className="font-normal text-sm leading-6 tracking-normal bg-yedllow-200">
                            {t('Manage the topics assigned to your agent.')}
                        </div>
                    </div>
                    )}
                    {/* Only show Instructions & Actions for new agents */}
                    {creationType === 'new' && (
                        <div className="md:w-[70%] flex flex-col gap-3">
                            <div className='flex flex-row justify-between items-center'>
                                <div className='text-[var(--Gray-700)] font-poppins text-sm font-semibold leading-6'>Instructions & Actions</div>
                                <CtyButton className={'border-1 font-medium'} variant={'bordered'} radius='sm' size={'sm'} color={'default'} startContent={<Icon icon="fa6-solid:plus" width="12" height="15" style={{ color: 'primary' }} />} onPress={handleAddInstruction} isDisabled={!isCreateMode && !canUpdate}>
                                    {t('New Instruction')}
                                </CtyButton>
                            </div>
                            {instructions.map((inst, idx) => (
                                <div key={idx + 'instruction'} className="border p-3 rounded-lg space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium">Instruction #{idx + 1}</span>
                                        {handleRemoveInstruction(idx)}
                                    </div>
                                    <Textarea
                                        id={`goal-${idx}`}
                                        minRows={3}
                                        aria-label={`goal-${idx}`}
                                        placeholder={t('Enter instruction')}
                                        value={inst.goal}
                                        onValueChange={val => updateInstruction(idx, 'goal', val)}
                                    />
                                    <Select
                                        id={`tools-${idx}`}
                                        placeholder={t('Select a tool')}
                                        size="sm"
                                        aria-label={`tools-${idx}`}
                                        selectedKeys={new Set([inst.tools])}
                                        disallowEmptySelection={true}
                                        onSelectionChange={keys => updateInstruction(idx, 'tools', String([...keys][0]))}
                                    >
                                        {toolsList.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </Select>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal">
                                            {t('Include action in output')}
                                        </span>
                                        <Switch
                                            id={`include-response-${idx}`}
                                            size="sm"
                                            aria-label={`include-response-${idx}`}
                                            isSelected={inst.include_in_response ?? false}
                                            onValueChange={val => updateInstruction(idx, 'include_in_response', val)}
                                        />
                                    </div>
                                </div>
                            ))}

                        </div>
                    )}

                    <div className='flex justify-end md:w-[70%]'>
                        <CtyButton variant="solid" color="primary" size="md" radius="sm" onPress={handleSave} isLoading={isAgentCreationLoading || isAgentUpdateLoading || isCreatingVoiceAgent} isDisabled={!isCreateMode && !canUpdate}>
                            {mode === RouteState.CREATE ? 'Save' : 'Update'}
                        </CtyButton>
                    </div>
                </div>
            </div>


        </div>
    );
}

export default AgentCreation;